import React, { MouseEventHandler, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Route, getRouteHealthStatuses, Organization, ProviderType, DestinationType, Destination } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, filterClasses, paginationClasses, skeletonClasses } from "../common/tailwindClassnames";
import { CopyToClipboard, ArrowForward, RefreshSpinner } from "../common/ProjectIcons";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import { getRouteStatus } from "./getRouteStatus";
import { routeDisplayName } from "./routeDisplayName";
import { routeRecordId } from "./routeDisplayName";
import { HoverTooltip } from "../common/HoverTooltip";
import SelectInput from "../common/SelectInput";
import SortByDropdown, { SortOption } from "../common/SortByDropdown";

const getHostname = (url: string): string => {
    try { return new URL(url).hostname; } catch { return url; }
};

type DestWithDisplay = Destination & { displayUrl: string, hasUrl: boolean };

const DestinationCellContent = React.memo(({ destinations, t }: {
    destinations: Destination[],
    t: (key: string, opts?: any) => string,
}) => {
    const groups = useMemo(() => {
        const withDisplay: DestWithDisplay[] = destinations.map((dest) => {
            if (!dest.base_url.trim()) return { ...dest, displayUrl: dest.name, hasUrl: false };
            let displayUrl = dest.base_url.trim();
            try { displayUrl = new URL(dest.base_url).hostname; } catch {}
            return { ...dest, displayUrl, hasUrl: true };
        });
        return withDisplay.reduce((acc: Record<string, DestWithDisplay[]>, dest) => {
            if (!acc[dest.type.name]) acc[dest.type.name] = [];
            acc[dest.type.name].push(dest);
            return acc;
        }, {});
    }, [destinations]);

    const MAX_VISIBLE = 2;
    return (
        <>
            {Object.entries(groups).map(([typeName, dests]) => {
                const visible = dests.slice(0, MAX_VISIBLE);
                const hiddenCount = dests.length - visible.length;
                return (
                    <div key={typeName} className="mb-1 last:mb-0">
                        <span className="block text-base font-normal text-grey-g0">{typeName}</span>
                        {visible.map((dest) => (
                            <div key={dest.id} className="flex items-center gap-1 min-w-0">
                                <span className="text-xs text-secondary-gray truncate" title={dest.hasUrl ? dest.base_url : dest.name}>
                                    {dest.displayUrl}
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(dest.hasUrl ? dest.base_url : dest.name);
                                        toast.info(`${t("copied to clipboard")}`, {
                                            position: toast.POSITION.TOP_RIGHT,
                                            autoClose: 2000
                                        });
                                    }}
                                    className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                    aria-label={t("Copy to clipboard")}
                                >
                                    <CopyToClipboard />
                                </button>
                            </div>
                        ))}
                        {hiddenCount > 0 && (
                            <HoverTooltip
                                position="right"
                                align="left"
                                width="w-52"
                                content={
                                    <div className="text-xs flex flex-col gap-1">
                                        {dests.slice(MAX_VISIBLE).map((d) => (
                                            <span key={d.id} className="text-secondary-gray truncate" title={d.hasUrl ? d.base_url : d.name}>
                                                {d.displayUrl}
                                            </span>
                                        ))}
                                    </div>
                                }
                            >
                                <span
                                    className="text-xs text-accent-green font-normal leading-normal underline cursor-default"
                                    style={{ textDecorationSkipInk: 'none', textUnderlinePosition: 'from-font' }}
                                >
                                    {t("+ {{count}} other", { count: hiddenCount })}
                                </span>
                            </HoverTooltip>
                        )}
                    </div>
                );
            })}
        </>
    );
});
DestinationCellContent.displayName = 'DestinationCellContent';

const HealthLegend = ({ t }: { t: (key: string) => string }) => (
    <div className="text-xs text-left">
        <p className="font-semibold mb-1 text-off-black">{t("Route Health")}</p>
        <p className="mb-1.5 text-secondary-gray">{t("Routes are analyzed every hour.")}</p>
        {(["healthy", "unhealthy", "disabled"] as const).map((status) => {
            const s = getRouteStatus(status, false, t);
            return (
                <div key={status} className="flex items-center gap-1.5 mb-1">
                    <span className="flex-shrink-0">{s.icon}</span>
                    <span className="text-secondary-gray">{s.text}</span>
                </div>
            );
        })}
    </div>
);

type RoutesProps = {
    canAddRoute: boolean,
    routes: Route[],
    reloadRoutes: boolean,
    organizations: Organization[],
    destinationUrls: string[],
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onSelectRoute: Function,
    onChangeGlobalSearch: (value: string) => void,
    onRefresh: () => void,
    onChangeStatusFilter: Function,
    statusFilterValue: any,
    onChangeDestinationUrl: Function,
    destinationFilterValue: any,
    loadingDestinationUrls: boolean,
    onChangeOwner: Function,
    ownerFilterValue: any,
    loadingOrganizations: boolean,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    globalSearchStr: string,
    ordering: string,
    onChangeSortBy: (selected: SortOption) => void,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    onClickAdd: MouseEventHandler
}

const RoutesList = ({
    canAddRoute,
    routes,
    reloadRoutes,
    organizations,
    destinationUrls,
    providerTypes,
    loadingProviderTypes,
    destinationTypes,
    loadingDestinationTypes,
    onSelectRoute,
    onChangeGlobalSearch,
    onRefresh,
    onChangeStatusFilter,
    statusFilterValue,
    onChangeDestinationUrl,
    destinationFilterValue,
    loadingDestinationUrls,
    onChangeOwner,
    ownerFilterValue,
    loadingOrganizations,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    globalSearchStr,
    ordering,
    onChangeSortBy,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    onClickAdd
}: RoutesProps) => {
    const { t } = useTranslation();

    const sortOptions: SortOption[] = useMemo(() => [
        { label: t("Name (A-Z)"), value: "name" },
        { label: t("Name (Z-A)"), value: "-name" },
        { label: t("Workspace"), value: "owner__name" },
        { label: t("Technology"), value: "type__name" },
    ], [t]);

    const hasActiveFilters = !!(globalSearchStr || statusFilterValue || destinationFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeStatusFilter(null);
        onChangeDestinationUrl(null);
        onChangeOwner(null);
        onChangeProviderType(null);
        onChangeDestinationType(null);
    };

    const routeHealthStatuses = useMemo(() => getRouteHealthStatuses(t), [t]);
    const destinationUrlOptions = useMemo(() => destinationUrls.map(url => {
        let hostname = url;
        try { hostname = new URL(url).hostname; } catch {}
        return { label: hostname, value: url };
    }), [destinationUrls]);

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between max-w-list-table mx-auto">
                <div>
                    <h1 data-testid="routes_page_header" className={textClasses.heading}>
                        {t("Routes")}
                    </h1>
                </div>
                {canAddRoute && <button className={buttonClasses.primary}
                    onClick={onClickAdd}
                >
                    {t("Create Route")}
                </button>}
            </div>
            <div className="max-w-list-table mx-auto">
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 mb-3">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} savedString={globalSearchStr} />
                <SortByDropdown options={sortOptions} ordering={ordering} onChangeSortBy={onChangeSortBy} />
                {hasActiveFilters && (
                    <button className={`${buttonClasses.lightGreen} whitespace-nowrap h-[38px] mb-2 ml-auto`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onRefresh()}
                    aria-label={t("Refresh list")}
                    disabled={reloadRoutes}
                    className={`flex justify-center items-center w-8 h-8 cursor-pointer self-end mb-1 ${hasActiveFilters ? '' : 'ml-auto'} ${reloadRoutes ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                >
                    <RefreshSpinner />
                </button>
            </div>
            {/* Filter dropdowns row */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* Provider Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Data Provider")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className=""
                        variant="routeFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className=""
                        variant="routeFilter"
                    />
                </div>
                {/* Destination */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination URL")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrlOptions}
                        value={destinationFilterValue}
                        onChange={onChangeDestinationUrl}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className=""
                        variant="routeFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className=""
                        variant="routeFilter"
                    />
                </div>
                {/* Health */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Health")}</span>
                    <SelectInput
                        name="status"
                        options={routeHealthStatuses.map(s => ({ label: s.name, value: s.value }))}
                        value={statusFilterValue}
                        onChange={onChangeStatusFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={false}
                        isDisabled={false}
                        isLoading={false}
                        className=""
                        variant="routeFilter"
                    />
                </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col w-full gap-2">
                {(!pageLoaded || reloadRoutes) ? (
                    [...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white border border-gray-lines rounded-lg p-4" data-testid="loading">
                            {/* Title row: name + health icon */}
                            <div className="flex items-center gap-2 mb-3">
                                <div className={`h-5 w-48 ${skeletonClasses.base}`} />
                                <div className={`h-5 w-5 ${skeletonClasses.base} rounded-full flex-shrink-0`} />
                            </div>
                            {/* Content row */}
                            <div className="flex items-start gap-4">
                                {/* Source */}
                                <div className="flex flex-col gap-1 w-40 flex-shrink-0">
                                    <div className={`h-4 w-28 ${skeletonClasses.base}`} />
                                    <div className={`h-3 w-24 ${skeletonClasses.base}`} />
                                </div>
                                {/* Arrow */}
                                <div className={`h-5 w-5 ${skeletonClasses.base} flex-shrink-0 mt-0.5`} />
                                {/* Destination */}
                                <div className="flex flex-col gap-1 w-64">
                                    <div className={`h-4 w-24 ${skeletonClasses.base}`} />
                                    <div className={`h-3 w-32 ${skeletonClasses.base}`} />
                                </div>
                                {/* Workspace */}
                                <div className={`h-4 w-28 ${skeletonClasses.base} flex-shrink-0`} />
                            </div>
                        </div>
                    ))
                ) : routes.length === 0 ? (
                    <NoResults
                        headerMsg={t("No Results Found")}
                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                    />
                ) : (
                    routes.map((route: Route) => {
                        const routeStatus = getRouteStatus(route.status, false, t);
                        // Route pages are addressed by the route RECORD's id, not the
                        // provider integration's — falling back to the latter sent the
                        // user to a 404, since /v2/routes/{integrationId} doesn't exist.
                        const recordId = routeRecordId(route);
                        // An unhealthy Route opens on its Logs tab — the logs are
                        // where the reason lives, so start the user there.
                        const subpage = route.status === "unhealthy" ? "logs" : undefined;
                        // No record means no Route page to open — the card stays
                        // informative rather than pretending to be a link.
                        const navigateToRoute = (e: React.MouseEvent) => {
                            if (!recordId) return;
                            if (e.metaKey || e.ctrlKey) {
                                window.open(`/routes/${recordId}${subpage ? `/${subpage}` : ""}`, '_blank', 'noopener,noreferrer');
                            } else {
                                onSelectRoute(recordId, subpage);
                            }
                        };
                        return (
                            <div
                                key={route.id}
                                className={`bg-white border border-gray-lines rounded-lg p-4 ${recordId ? "cursor-pointer" : "cursor-default"}`}
                                role={recordId ? "button" : undefined}
                                tabIndex={recordId ? 0 : undefined}
                                onClick={navigateToRoute}
                                onKeyDown={(e: React.KeyboardEvent) => { if (recordId && e.key === 'Enter') { e.preventDefault(); onSelectRoute(recordId, subpage); } }}
                            >
                                {/* Title row: name + health */}
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-route-green text-[18px] font-medium truncate" title={routeDisplayName(route)}>
                                        {routeDisplayName(route)}
                                    </span>
                                    <HoverTooltip
                                        position="right"
                                        align="left"
                                        width="w-52"
                                        className="flex-shrink-0"
                                        ariaLabel={routeStatus.text}
                                        content={<HealthLegend t={t} />}
                                    >
                                        {routeStatus.icon}
                                    </HoverTooltip>
                                </div>
                                {/* Content row: source → destination + workspace */}
                                <div className="flex items-start gap-4">
                                    {/* Source */}
                                    <div className="flex flex-col min-w-0 w-40 flex-shrink-0">
                                        <span className="text-base font-normal text-grey-g0">{route.provider.type.name}</span>
                                        {route.provider.base_url && (
                                            <span className="text-xs text-secondary-gray truncate" title={route.provider.base_url}>
                                                {getHostname(route.provider.base_url)}
                                            </span>
                                        )}
                                    </div>
                                    {/* Arrow */}
                                    <div className="flex-shrink-0 mt-0.5">
                                        <ArrowForward size={20} className="text-secondary-gray" />
                                    </div>
                                    {/* Destination */}
                                    <div className="w-[280px] min-w-0 overflow-hidden">
                                        {route.destinations.length === 0 ? (
                                            <span className="text-sm text-secondary-gray">{t("Destination not set")}</span>
                                        ) : (
                                            <DestinationCellContent destinations={route.destinations} t={t} />
                                        )}
                                    </div>
                                    {/* Workspace */}
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                        <span className="text-sm text-secondary-gray truncate block" title={route.owner.name}>{route.owner.name}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                {totalCount > 0 && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div>
        </div>
    )
};

export default RoutesList;
